import { useEffect, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import { DynamicForm } from "../../../dynamicForm";
import type { RegistrationById } from "../../../api/registrations.types";
import { useQuery } from "@tanstack/react-query";
import { responsibleCandidatesQueryOptions, type useSetResponsible } from "../../../api/registrations";
import { useRegistrationVariant } from "../RegistrationVariantProvider";
import type { AutocompleteOptionType } from "../../../shared/Autocomplete";
import {
  AUTOCOMPLETE_LOADING_OPTION,
  AutocompleteOption,
  createFilterAutocompleteOption,
} from "../../../shared/Autocomplete";
import { PersonIcon3, TriangleAlertIcon } from "../../../shared/icons";
import { useGetSortByKey } from "../../../shared/SortByKey";

type SelectableResponsibleUser = { userId: string; firstName: string; lastName: string; email: string };

type ResponsibleFieldProps = {
  responsible: RegistrationById["responsible"];
  isReadOnly: boolean;
  isSaving: boolean;
  hasError: boolean;
  selectableUsers: Array<SelectableResponsibleUser> | undefined;
  onSelectableUsersNeeded: () => void;
  onSelect: (userId: string, onError: () => void) => void;
  noSelectableUsersText?: string;
};

export const RESPONSIBLE_FIELD_ID = "responsibleUser";

export const ResponsibleField: FC<ResponsibleFieldProps> = props => {
  const { t } = useTranslation();

  return (
    <DynamicForm.InlineFieldPrimary
      htmlFor={RESPONSIBLE_FIELD_ID}
      label={t("RegistrationDrawer.Responsible.Label")}
      infoPopoverContent={{
        title: t("RegistrationDrawer.Responsible.InfoPopoverHeader"),
        body: t("RegistrationDrawer.Responsible.InfoPopoverContent"),
      }}
      input={
        <Stack sx={{ width: "100%" }}>
          <ResponsibleInput {...props} />
          {props.hasError && <DynamicForm.ErrorMessage />}
        </Stack>
      }
    />
  );
};

const ResponsibleInput: FC<ResponsibleFieldProps> = ({
  responsible,
  isReadOnly,
  isSaving,
  hasError,
  selectableUsers,
  onSelectableUsersNeeded,
  onSelect,
  noSelectableUsersText,
}) => {
  const { t } = useTranslation();
  const { inputState, setIsEditing } = DynamicForm.useInlineInputState(isReadOnly);
  const sortByKey = useGetSortByKey();
  const [selectedOption, setSelectedOption] = useState<AutocompleteOptionType>(() => responsibleToOption(responsible));

  useEffect(() => {
    setSelectedOption(responsibleToOption(responsible));
  }, [responsible]);

  let options: AutocompleteOptionType[] = [AUTOCOMPLETE_LOADING_OPTION];
  if (selectableUsers) {
    options = sortByKey(
      selectableUsers.map(user => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.userId,
        description: user.email,
      })),
      "label"
    );
  }

  const handleSelect = (newOption: AutocompleteOptionType) => {
    const previousOption = selectedOption;
    setSelectedOption(newOption);
    onSelect(newOption.value, () => setSelectedOption(previousOption));
  };

  return (
    <Autocomplete
      noOptionsText={
        selectableUsers?.length === 0 && noSelectableUsersText
          ? noSelectableUsersText
          : t("DynamicForm.PersonSelect.Empty")
      }
      id={RESPONSIBLE_FIELD_ID}
      forcePopupIcon
      blurOnSelect
      onMouseOver={onSelectableUsersNeeded}
      value={selectedOption}
      onBlur={() => setIsEditing(false)}
      onFocus={() => {
        setIsEditing(true);
        onSelectableUsersNeeded();
      }}
      disableClearable
      disabled={isReadOnly || isSaving}
      options={options}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return <AutocompleteOption key={option.value} dense option={option} {...optionProps} />;
      }}
      onChange={(_, option) => handleSelect(option)}
      clearOnBlur
      getOptionDisabled={option => option.value === selectedOption.value}
      isOptionEqualToValue={(option, selected) =>
        option.value === AUTOCOMPLETE_LOADING_OPTION.value || option.value === selected.value
      }
      filterOptions={createFilterAutocompleteOption}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          placeholder={responsible ? undefined : t("RegistrationGrid.NoResponsible")}
          sx={{
            "& input::placeholder": {
              color: theme => theme.alpha(theme.palette.warning.dark, 0.6),
              WebkitTextFillColor: theme => theme.alpha(theme.palette.warning.dark, 0.6),
              opacity: 1,
            },
          }}
          error={hasError}
          slotProps={{
            ...params.slotProps,

            input: {
              ...params.slotProps.input,
              sx: DynamicForm.inlineInputStateStyles[inputState],
              startAdornment: responsible ? (
                <PersonIcon3 size={16} />
              ) : (
                <TriangleAlertIcon size={16} color={theme => theme.palette.warning.main} />
              ),
            },
          }}
        />
      )}
    />
  );
};

const responsibleToOption = (responsible: RegistrationById["responsible"]) =>
  responsible
    ? {
        label: `${responsible.firstName} ${responsible.lastName}`,
        value: responsible.userId,
        description: responsible.email,
      }
    : { label: "", value: "" };

type RegistrationResponsibleProps = {
  registration: RegistrationById;
  isReadOnly: boolean;
  setResponsibleMutation: ReturnType<typeof useSetResponsible>;
};

export const RegistrationResponsible: FC<RegistrationResponsibleProps> = ({
  registration,
  isReadOnly,
  setResponsibleMutation,
}) => {
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const [areSelectableUsersNeeded, setAreSelectableUsersNeeded] = useState(false);
  const candidatesQuery = useQuery({
    ...responsibleCandidatesQueryOptions(variant, registration.id),
    enabled: areSelectableUsersNeeded && !isReadOnly,
  });

  return (
    <ResponsibleField
      responsible={registration.responsible}
      isReadOnly={isReadOnly}
      isSaving={setResponsibleMutation.isPending}
      hasError={setResponsibleMutation.isError}
      selectableUsers={candidatesQuery.data?.users}
      noSelectableUsersText={
        registration.connectedTenants.length > 1
          ? t("RegistrationDrawer.Responsible.NoCandidates.Plural")
          : t("RegistrationDrawer.Responsible.NoCandidates.Singular")
      }
      onSelectableUsersNeeded={() => setAreSelectableUsersNeeded(true)}
      onSelect={(userId, onError) => setResponsibleMutation.mutate(userId, { onError })}
    />
  );
};
